import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import toast from "react-hot-toast";

import { PAGE_CONTENT_CLASS } from "components/layout/PageContent";
import PageHeader from "components/PageHeader";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import LoadingMark from "components/ui/LoadingMark";

import customerService from "services/customerService";
import CustomerFormModal from "./CustomerFormModal";
import {
  normalizeCustomersList,
  getCustomerId,
  getCustomerNome,
  getCustomerCpf,
  unwrapCustomerDetail,
} from "utils/customerApiHelpers";
import { maskCpf } from "utils/masks";

const PAGE_SIZE = 10;

function getCustomerCreatedAt(item) {
  return (
    item?.created_at ??
    item?.data_criacao ??
    item?.DATA_CRIACAO ??
    item?.created_date ??
    null
  );
}

function formatCreatedAt(value) {
  if (value == null || value === "") return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const CustomersPage = () => {
  const [customers, setCustomers] = useState([]);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [editingId, setEditingId] = useState(null);
  const [editInitialPayload, setEditInitialPayload] = useState(null);
  const [fetchingCustomerId, setFetchingCustomerId] = useState(null);

  const loadCustomers = useCallback(async () => {
    setLoading(true);
    setListError(null);

    const result = await customerService.getCustomers();

    if (!result.success) {
      setListError(
        typeof result.error === "string"
          ? result.error
          : result.error?.message || "Erro ao carregar clientes"
      );
      setCustomers([]);
      setLoading(false);
      return;
    }

    const list = normalizeCustomersList(result.data);
    setCustomers(list);
    setVisibleCount(PAGE_SIZE);
    setLoading(false);
  }, []);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  const openCreate = () => {
    setModalMode("create");
    setEditingId(null);
    setEditInitialPayload(null);
    setModalOpen(true);
  };

  const openEdit = async (id) => {
    setFetchingCustomerId(id);
    try {
      const result = await customerService.getCustomer(id);

      if (!result.success) {
        toast.error(
          typeof result.error === "string"
            ? result.error
            : "Não foi possível carregar o cliente"
        );
        return;
      }

      const detail = unwrapCustomerDetail(result.data);
      if (!detail || typeof detail !== "object") {
        toast.error("Dados do cliente inválidos.");
        return;
      }

      setModalMode("edit");
      setEditingId(id);
      setEditInitialPayload(detail);
      setModalOpen(true);
    } finally {
      setFetchingCustomerId(null);
    }
  };

  const handleSave = async (payload) => {
    const result = await customerService.saveCustomer(payload);

    if (!result.success) {
      const msg =
        typeof result.error === "string"
          ? result.error
          : result.error?.message || "Erro ao salvar cliente";
      throw new Error(msg);
    }

    toast.success(
      modalMode === "edit" ? "Cliente atualizado." : "Cliente cadastrado."
    );
    await loadCustomers();
  };

  const handleDelete = async (id, nome) => {
    const ok = window.confirm(
      `Excluir o cliente${nome ? ` "${nome}"` : ""}? Esta ação não pode ser desfeita.`
    );
    if (!ok) return;

    const result = await customerService.deleteCustomer(id);

    if (!result.success) {
      toast.error(
        typeof result.error === "string"
          ? result.error
          : "Erro ao excluir cliente"
      );
      return;
    }

    toast.success("Cliente excluído.");
    await loadCustomers();
  };

  const visibleRows = customers.slice(0, visibleCount);
  const showLoadMore =
    customers.length > PAGE_SIZE && visibleCount < customers.length;

  return (
    <>
      <div className={PAGE_CONTENT_CLASS}>
        <Helmet>
          <title>Clientes — neo-modelos</title>
        </Helmet>

        <PageHeader
          title="Clientes"
          subtitle="Cadastre e gerencie clientes para usar nos documentos"
          icon="Users"
          action={
            <Button
              type="button"
              onClick={openCreate}
              className="gap-2 w-full sm:w-auto"
            >
              <Icon name="UserPlus" size={18} />
              Adicionar cliente
            </Button>
          }
        />

        {loading && (
          <div className="flex justify-center py-10">
            <LoadingMark message="Carregando clientes…" />
          </div>
        )}

        {!loading && listError && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
            {listError}
          </div>
        )}

        {!loading && !listError && customers.length === 0 && (
          <div className="rounded-lg border border-border bg-card p-8 text-center text-muted-foreground">
            Nenhum cliente cadastrado ainda.
          </div>
        )}

        {!loading && !listError && customers.length > 0 && (
          <>
            <div className="hidden md:block overflow-x-auto rounded-lg border border-border bg-card">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/30 text-left">
                    <th className="px-4 py-3 font-medium">Nome</th>
                    <th className="px-4 py-3 font-medium">CPF</th>
                    <th className="px-4 py-3 font-medium whitespace-nowrap">
                      Data de criação
                    </th>
                    <th className="px-4 py-3 font-medium text-right w-[120px]">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((row) => {
                    const id = getCustomerId(row);
                    const nome = getCustomerNome(row);
                    const cpf = getCustomerCpf(row);
                    const cpfDisplay = cpf ? maskCpf(cpf) : "";
                    const created = getCustomerCreatedAt(row);

                    return (
                      <tr
                        key={id || `${nome}-${cpf}`}
                        className="border-b border-border last:border-0 hover:bg-muted/20"
                      >
                        <td className="px-4 py-3 font-medium">{nome || "—"}</td>
                        <td className="px-4 py-3 tabular-nums">
                          {cpfDisplay || "—"}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                          {formatCreatedAt(created)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-9 w-9"
                              title="Editar"
                              aria-label="Editar cliente"
                              onClick={() => id && openEdit(id)}
                              disabled={!id || fetchingCustomerId === id}
                            >
                              <Icon
                                name={
                                  fetchingCustomerId === id
                                    ? "Loader2"
                                    : "Pencil"
                                }
                                size={18}
                                className={
                                  fetchingCustomerId === id
                                    ? "animate-spin"
                                    : undefined
                                }
                              />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-9 w-9 text-destructive hover:text-destructive"
                              title="Excluir"
                              aria-label="Excluir cliente"
                              onClick={() => id && handleDelete(id, nome)}
                              disabled={!id}
                            >
                              <Icon name="Trash2" size={18} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="md:hidden space-y-4">
              {visibleRows.map((row) => {
                const id = getCustomerId(row);
                const nome = getCustomerNome(row);
                const cpf = getCustomerCpf(row);
                const cpfDisplay = cpf ? maskCpf(cpf) : "";
                const created = getCustomerCreatedAt(row);

                return (
                  <div
                    key={id || `${nome}-${cpf}`}
                    className="rounded-lg border border-border bg-card p-4 shadow-elevation-sm"
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div className="flex items-center justify-center w-11 h-11 rounded-lg bg-primary/10 shrink-0">
                        <Icon name="User" size={22} className="text-primary" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-semibold text-foreground truncate">
                          {nome || "—"}
                        </h3>
                        <p className="text-sm text-muted-foreground tabular-nums mt-0.5">
                          {cpfDisplay || "CPF não informado"}
                        </p>
                      </div>
                    </div>

                    <p className="text-xs text-muted-foreground mb-4">
                      Criado em {formatCreatedAt(created)}
                    </p>

                    <div className="flex gap-2 pt-3 border-t border-border">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="flex-1 gap-2"
                        onClick={() => id && openEdit(id)}
                        disabled={!id || fetchingCustomerId === id}
                      >
                        <Icon
                          name={
                            fetchingCustomerId === id ? "Loader2" : "Pencil"
                          }
                          size={16}
                          className={
                            fetchingCustomerId === id ? "animate-spin" : undefined
                          }
                        />
                        Editar
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="flex-1 gap-2 text-destructive hover:text-destructive"
                        onClick={() => id && handleDelete(id, nome)}
                        disabled={!id}
                      >
                        <Icon name="Trash2" size={16} />
                        Excluir
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>

            {showLoadMore && (
              <div className="flex justify-center mt-8">
                <Button
                  type="button"
                  onClick={() =>
                    setVisibleCount((v) => Math.min(v + PAGE_SIZE, customers.length))
                  }
                  className="w-full sm:w-auto min-w-[140px]"
                >
                  Mostrar mais
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      <CustomerFormModal
        isOpen={modalOpen}
        mode={modalMode}
        customerId={editingId}
        initialFormState={modalMode === "edit" ? editInitialPayload : null}
        onClose={() => {
          setModalOpen(false);
          setEditingId(null);
          setEditInitialPayload(null);
        }}
        onSaved={handleSave}
      />
    </>
  );
};

export default CustomersPage;
